import { useTranslation } from "react-i18next";
import { format, differenceInMinutes } from "date-fns";
import { useCollectionData } from "react-firebase-hooks/firestore";
import { query, where } from "firebase/firestore";
import OnixFileIcon from "~/assets/svg/onix-file-icon.svg?react";
import { Button } from "~/components/button";
import { Badge } from "~/components/ui/badge";
import { Loader } from "~/components/loading/loader";
import { Progress } from "~/components/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";

import { Error } from "~/components/icon/error";
import {
  getPipelineReportCollection,
  PipelineReportWithId,
} from "~/integrations/firebase/collections";
import { Text } from "~/components/text";

export const OrganisationOnixUploadFile = ({
  fileName,
  fileType = "Onix",
  organisationId,
  fileSizeMB,
  createdAt,
  onActionClick,
  dummy,
  report: externalReport,
}: {
  fileName: string;
  fileType?: string;
  organisationId: string;
  fileSizeMB?: number;
  createdAt?: Date | null;
  onActionClick?: (reportId: string) => void;
  dummy?: boolean;
  report?: PipelineReportWithId;
}) => {
  const { t } = useTranslation(["settings"]);

  const [[fetchedReport] = [], fetchLoading] = useCollectionData(
    externalReport
      ? undefined
      : query(
          getPipelineReportCollection(),
          where("organisation.id", "==", organisationId),
          where("fileName", "==", fileName),
        ),
  );

  const report = externalReport ?? fetchedReport;
  const loading = externalReport ? false : fetchLoading;

  const failed = report?.productsFailed || 0;
  const succeeded = report?.productsSucceeded || 0;
  const progress = report?.progress || 0;
  const completedAt = report?.completedAt;

  const getState = (): "loading" | "timedOut" | "progress" | "completed" => {
    const isPotentiallyStalled = createdAt
      ? differenceInMinutes(new Date(), createdAt) > 5
      : false; // Calculate stall status once

    if (loading || dummy) {
      return "loading";
    }

    if (!report && !isPotentiallyStalled) {
      return "loading";
    }

    if (!report && isPotentiallyStalled) {
      return "timedOut";
    }

    if (progress === 1) {
      return "completed";
    }

    if (completedAt) {
      return "completed";
    }

    return "progress";
  };

  const state = getState();

  return (
    <li
      className="flex border-secondary-200 border rounded-xl p-3 justify-between items-center"
      aria-label={fileName}
    >
      <div className="flex gap-3">
        <div className="border-secondary-200 border rounded-lg size-11 flex items-center justify-center">
          <OnixFileIcon />
        </div>
        <div className="flex flex-col justify-center">
          <Text variant="label">{fileName}</Text>
          <Text variant="caption" className="mt-1">
            {fileType} &middot;{" "}
            {fileSizeMB ? `${fileSizeMB.toFixed(2)} MB ·` : ""}{" "}
            {format(createdAt || new Date(), "P")}
          </Text>
        </div>
      </div>
      <div className="flex gap-3 items-center">
        {failed > 0 ? (
          <Text
            as="div"
            variant="caption"
            className="flex whitespace-nowrap items-center"
          >
            {t("settings:organisation.onix.misc.books", {
              count: failed,
            })}
            <Badge variant="error" className="ml-2">
              {t("settings:organisation.onix.misc.badge.failed")}
            </Badge>
          </Text>
        ) : null}
        {succeeded > 0 ? (
          <Text
            as="div"
            variant="caption"
            className="flex whitespace-nowrap items-center"
          >
            {t("settings:organisation.onix.misc.books", {
              count: succeeded,
            })}
            <Badge variant="success" className="ml-2">
              {t("settings:organisation.onix.misc.badge.success")}
            </Badge>
          </Text>
        ) : null}
        {state === "timedOut" ? (
          <Tooltip>
            <TooltipTrigger>
              <Error pulse={false} iconSize="small" data-testid="error-icon" />
            </TooltipTrigger>
            <TooltipContent>
              <div className="flex flex-col items-start">
                {t("settings:organisation.onix.misc.error")}
              </div>
            </TooltipContent>
          </Tooltip>
        ) : null}
        {state === "loading" ? (
          <Loader className="size-6 mr-2 border-2" data-testid="loader" />
        ) : null}
        {state === "progress" ? (
          <Progress
            value={progress * 100}
            className="w-48 h-2 bg-secondary-200"
          />
        ) : null}
        <Button
          variant="tertiary"
          size="small"
          icon="move-up-right"
          iconPosition="right"
          onClick={
            report && onActionClick ? () => onActionClick(report.id) : undefined
          }
          disabled={
            !report ||
            state !== "completed" ||
            // Legacy reports miss the `totalFiles` property and are incompatible
            !report.totalFiles
          }
        >
          {t("settings:organisation.onix.file.button.report")}
        </Button>
      </div>
    </li>
  );
};
